import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import {
  Activity, Brain, ListTodo, FlaskConical, FileText, MessageSquare, Lightbulb,
  Clock, CheckCircle2, AlertTriangle, TrendingUp, TrendingDown, Minus,
  Upload, HelpCircle, ChevronRight, StickyNote, Eye, Clipboard, CalendarDays,
  Shield, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { TwinFloStaffPanel } from '@/components/TwinFloCards';
import { TwinFloPredictionPanel, TwinFloTimeline, TwinFloSignature } from '@/components/TwinFloT2Cards';
import TrackingObservationsCard from '@/components/TrackingObservationsCard';

const stateColors = {
  stable: { bg: 'bg-[#4E7A6B]/20', text: 'text-[#4E7A6B]', border: 'border-[#4E7A6B]/30', dot: 'bg-[#4E7A6B]' },
  watch: { bg: 'bg-amber-500/20', text: 'text-amber-400', border: 'border-amber-500/30', dot: 'bg-amber-400' },
  at_risk: { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30', dot: 'bg-red-400' },
};

const notePriorityColors = {
  urgent: 'bg-red-500/20 text-red-400', high: 'bg-orange-500/20 text-orange-400',
  normal: 'bg-slate-500/20 text-slate-400', low: 'bg-slate-500/10 text-slate-500',
};

const noteTypeLabels = {
  general: 'General', adherence: 'Adherence', symptom_concern: 'Symptom',
  counselor_interpretation: 'Interpretation', follow_up_action: 'Follow-up',
};

export default function CareDashboard({ subscriberId, viewerRole, readOnly = false, onExit }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState({ text: '', note_type: 'general', priority: 'normal', visibility: 'care_team' });
  const [addingNote, setAddingNote] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);

  const isCareTeam = ['admin', 'counselor', 'health_coach', 'doctor'].includes(viewerRole);

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/care-dashboard/${subscriberId}`);
      setData(res.data);
    } catch (err) {
      console.error('Dashboard fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [subscriberId]);

  const fetchNotes = useCallback(async () => {
    if (!isCareTeam) return;
    try {
      const res = await axios.get(`${API}/care-notes/${subscriberId}`);
      setNotes(res.data);
    } catch (err) { /* ignore */ }
  }, [subscriberId, isCareTeam]);

  useEffect(() => { fetchDashboard(); fetchNotes(); }, [fetchDashboard, fetchNotes]);

  const handleAddNote = async () => {
    if (!newNote.text.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/care-notes/${subscriberId}`, newNote);
      toast.success('Care note added');
      setNewNote({ text: '', note_type: 'general', priority: 'normal', visibility: 'care_team' });
      setShowNoteForm(false);
      fetchNotes();
    } catch (err) {
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Clock className="w-6 h-6 text-white/30 animate-pulse" /></div>;
  }

  if (!data) {
    return <div className="text-center py-20 text-white/50">Unable to load dashboard data</div>;
  }

  const { current_state, current_phase, timeline_today, decode_summary, lab_snapshot, recent_report, insight, support_summary, last_checkin, protocol, subscriber } = data;
  const sc = stateColors[current_state?.state_level] || stateColors.stable;

  return (
    <div className="space-y-6" data-testid="care-dashboard">
      {/* Read-only impersonation banner */}
      {readOnly && (
        <div className="flex items-center justify-between p-3 rounded-lg bg-amber-500/10 border border-amber-500/20">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-amber-400" />
            <span className="text-sm text-amber-400">Read-only preview — viewing as {subscriber?.name}</span>
          </div>
          <Button variant="ghost" size="sm" className="text-amber-400 hover:text-amber-300" onClick={onExit} data-testid="exit-impersonation-btn">
            Exit Preview
          </Button>
        </div>
      )}

      {/* Row 1: Current State + Current Phase + Support Summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* A. Current State */}
        <Card className="bg-[#131B2B] border-white/10" data-testid="current-state-card">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs text-white/40 uppercase tracking-wider">Current State</p>
              <div className={`w-3 h-3 rounded-full ${sc.dot}`} />
            </div>
            <p className={`text-2xl font-['Outfit'] font-bold ${sc.text}`} data-testid="state-label">
              {current_state?.state_label || 'Stable'}
            </p>
            <p className="text-xs text-white/40 mt-1">{current_state?.state_reason}</p>
          </CardContent>
        </Card>

        {/* Current Phase */}
        <Card className="bg-[#131B2B] border-white/10" data-testid="current-phase-card">
          <CardContent className="p-5">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Current Phase</p>
            <p className="text-lg font-['Outfit'] font-semibold text-[#C89B7B]" data-testid="phase-label">
              {current_phase?.label || 'Onboarding'}
            </p>
            {current_phase?.active_protocol_phase && (
              <p className="text-xs text-white/40 mt-1">Protocol phase {current_phase.active_protocol_phase}</p>
            )}
          </CardContent>
        </Card>

        {/* Support Summary */}
        <Card className="bg-[#131B2B] border-white/10" data-testid="support-summary-card">
          <CardContent className="p-5">
            <p className="text-xs text-white/40 uppercase tracking-wider mb-3">Support</p>
            <div className="flex items-center gap-3">
              <div className={`text-2xl font-bold ${support_summary?.open_tickets > 0 ? 'text-amber-400' : 'text-[#4E7A6B]'}`}>
                {support_summary?.open_tickets || 0}
              </div>
              <p className="text-sm text-white/60">open tickets</p>
            </div>
            {support_summary?.latest_issue && (
              <p className="text-xs text-white/40 mt-2 truncate">{support_summary.latest_issue}</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* B. Timeline Today + Next Actions */}
      <Card className="bg-[#131B2B] border-white/10" data-testid="timeline-today-card">
        <CardHeader className="border-b border-white/10 pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-[#C89B7B]" />
              Today's Timeline
            </CardTitle>
            <Badge className="bg-white/10 text-white/60 text-xs capitalize">{timeline_today?.current_time_slot}</Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          {timeline_today?.total_today > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-4 text-sm">
                <span className="text-white/50">Completed: <span className="text-[#4E7A6B] font-medium">{timeline_today.completed_today}/{timeline_today.total_today}</span></span>
                <span className="text-white/50">Check-in: {timeline_today.has_checked_in ? <span className="text-[#4E7A6B]">Done</span> : <span className="text-amber-400">Pending</span>}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {timeline_today.next_actions?.map((action, i) => (
                  <div key={i} className="flex items-center gap-3 p-3 rounded-lg bg-[#0B0F19] border border-white/5">
                    <CheckCircle2 className="w-5 h-5 text-white/20 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm text-white truncate">{action.title}</p>
                      <p className="text-xs text-white/40">{action.timing}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-6">
              <ListTodo className="w-8 h-8 text-white/15 mx-auto mb-2" />
              <p className="text-sm text-white/40">No active protocol published yet</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tracking & Observations signal card (Tracking OS V1) */}
      <TrackingObservationsCard subscriberId={subscriberId} viewerRole={viewerRole} />

      {/* TwinFlo Intelligence Panel */}
      {isCareTeam && subscriberId && (
        <>
          <TwinFloStaffPanel subscriberId={subscriberId} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Card className="bg-adm-surface border-white/[0.06]">
              <CardContent className="p-3">
                <h4 className="text-[10px] text-adm-tertiary uppercase tracking-wider mb-2">Predictions</h4>
                <TwinFloPredictionPanel subscriberId={subscriberId} />
              </CardContent>
            </Card>
            <Card className="bg-adm-surface border-white/[0.06]">
              <CardContent className="p-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-[10px] text-adm-tertiary uppercase tracking-wider">Timeline</h4>
                  <a href={`/admin/twinflo-evolution/${subscriberId}`} className="text-[9px] text-adm-accent hover:underline flex items-center gap-0.5" data-testid="open-evolution">
                    Full view <ChevronRight className="w-3 h-3" />
                  </a>
                </div>
                <TwinFloTimeline subscriberId={subscriberId} />
              </CardContent>
            </Card>
            <Card className="bg-adm-surface border-white/[0.06]">
              <CardContent className="p-3">
                <h4 className="text-[10px] text-adm-tertiary uppercase tracking-wider mb-2">Signature</h4>
                <TwinFloSignature subscriberId={subscriberId} />
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Row 3: DECODE Summary + Lab Snapshot */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* D. DECODE Summary */}
        <Card className="lg:col-span-7 bg-[#131B2B] border-white/10" data-testid="decode-summary-card">
          <CardHeader className="border-b border-white/10 pb-3">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <Brain className="w-5 h-5 text-[#C89B7B]" /> DECODE Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {decode_summary ? (
              <div className="grid grid-cols-3 gap-3">
                <AxisCard label="BAS" text={decode_summary.bas_summary} color="[#C89B7B]" />
                <AxisCard label="NAS" text={decode_summary.nas_summary} color="[#4E7A6B]" />
                <AxisCard label="TAS" text={decode_summary.tas_summary} color="white/70" />
              </div>
            ) : (
              <EmptyState icon={Brain} text="No DECODE uploaded yet" />
            )}
          </CardContent>
        </Card>

        {/* E. Lab Snapshot */}
        <Card className="lg:col-span-5 bg-[#131B2B] border-white/10" data-testid="lab-snapshot-card">
          <CardHeader className="border-b border-white/10 pb-3">
            <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
              <FlaskConical className="w-5 h-5 text-[#4E7A6B]" /> Lab Snapshot
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {lab_snapshot?.has_labs ? (
              <div>
                <p className="text-xs text-white/40 mb-3">Last lab: {lab_snapshot.last_lab_date} ({lab_snapshot.total_markers} markers)</p>
                <div className="space-y-2">
                  {lab_snapshot.key_markers?.map((m, i) => (
                    <div key={i} className="flex items-center justify-between p-2 rounded bg-[#0B0F19]">
                      <span className="text-sm text-white/80">{m.name}</span>
                      <span className={`text-sm font-mono ${m.severity === 'critical' || m.severity === 'abnormal' ? 'text-red-400' : 'text-white/70'}`}>
                        {m.value} {m.unit}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <EmptyState icon={FlaskConical} text="No recent lab data available" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 4: Insight + Recent Report */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* H. Insight Card */}
        <Card className="bg-gradient-to-br from-[#131B2B] to-[#1A2235] border-[#C89B7B]/20" data-testid="insight-card">
          <CardContent className="p-5">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-xl bg-[#C89B7B]/20 flex items-center justify-center flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-[#C89B7B]" />
              </div>
              <div>
                <p className="text-xs text-[#C89B7B] uppercase tracking-wider mb-1">Insight</p>
                <p className="text-sm text-white/80 leading-relaxed">{insight || 'Insights will appear as more data is collected.'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* F. Recent Report */}
        <Card className="bg-[#131B2B] border-white/10" data-testid="recent-report-card">
          <CardContent className="p-5">
            {recent_report ? (
              <div>
                <p className="text-xs text-white/40 uppercase tracking-wider mb-2">Latest Report</p>
                <p className="font-medium text-white mb-1">{recent_report.title}</p>
                <p className="text-sm text-white/50 line-clamp-2 mb-3">{recent_report.summary}</p>
                <Button variant="outline" size="sm" className="border-white/10 text-white/70 hover:bg-white/5">
                  <FileText className="w-4 h-4 mr-1" /> View Report
                </Button>
              </div>
            ) : (
              <EmptyState icon={FileText} text="No 20-day report yet" />
            )}
          </CardContent>
        </Card>
      </div>

      {/* G. Quick Actions — role specific */}
      <Card className="bg-[#131B2B] border-white/10" data-testid="quick-actions-card">
        <CardHeader className="border-b border-white/10 pb-3">
          <CardTitle className="text-base font-['Outfit'] text-white">Quick Actions</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3">
            {viewerRole === 'subscriber' || viewerRole === 'caregiver' ? (
              <>
                <QuickAction icon={Clipboard} label="Log Today's Data" to="/tracking" />
                <QuickAction icon={Upload} label="Upload Report" to="/reports" />
                <QuickAction icon={HelpCircle} label="Ask Support" to="/support" />
              </>
            ) : (
              <>
                {!readOnly && (
                  <Button variant="outline" size="sm" className="border-white/10 text-white/70 hover:bg-white/5"
                    onClick={() => setShowNoteForm(!showNoteForm)} data-testid="add-care-note-btn">
                    <StickyNote className="w-4 h-4 mr-1" /> Add Care Note
                  </Button>
                )}
                <QuickAction icon={Eye} label="Review Adherence" to={`/admin/care-ops`} />
                <QuickAction icon={FileText} label="View Reports" to={`/admin/documents`} />
                <QuickAction icon={HelpCircle} label="Open Support" to={`/admin/care-ops`} />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Care Team Overlays */}
      {isCareTeam && !readOnly && (
        <div className="space-y-4">
          {/* Care Notes Panel */}
          {showNoteForm && (
            <Card className="bg-[#131B2B] border-[#C89B7B]/20" data-testid="note-form-card">
              <CardContent className="p-4 space-y-3">
                <p className="text-sm font-medium text-white">New Care Note</p>
                <div className="flex gap-3">
                  <Select value={newNote.note_type} onValueChange={v => setNewNote(p => ({ ...p, note_type: v }))}>
                    <SelectTrigger className="w-[140px] h-8 text-xs bg-[#0B0F19] border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#131B2B] border-white/10">
                      <SelectItem value="general">General</SelectItem>
                      <SelectItem value="adherence">Adherence</SelectItem>
                      <SelectItem value="symptom_concern">Symptom</SelectItem>
                      <SelectItem value="counselor_interpretation">Interpretation</SelectItem>
                      <SelectItem value="follow_up_action">Follow-up</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={newNote.priority} onValueChange={v => setNewNote(p => ({ ...p, priority: v }))}>
                    <SelectTrigger className="w-[100px] h-8 text-xs bg-[#0B0F19] border-white/10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#131B2B] border-white/10">
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="normal">Normal</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="urgent">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Textarea value={newNote.text} onChange={e => setNewNote(p => ({ ...p, text: e.target.value }))}
                  className="bg-[#0B0F19] border-white/10 text-white min-h-[80px]" placeholder="Write care note..."
                  data-testid="care-note-textarea" />
                <div className="flex justify-end gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setShowNoteForm(false)}>Cancel</Button>
                  <Button size="sm" className="copper-gradient text-[#0B0F19]" onClick={handleAddNote} disabled={addingNote}
                    data-testid="save-care-note-btn">
                    {addingNote ? 'Saving...' : 'Save Note'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes List */}
          {notes.length > 0 && (
            <Card className="bg-[#131B2B] border-white/10" data-testid="care-notes-panel">
              <CardHeader className="border-b border-white/10 pb-3">
                <CardTitle className="text-base font-['Outfit'] text-white flex items-center gap-2">
                  <StickyNote className="w-5 h-5 text-[#C89B7B]" />
                  Care Notes ({notes.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-3 space-y-2 max-h-[300px] overflow-y-auto">
                {notes.slice(0, 10).map(note => (
                  <div key={note.id} className="p-3 rounded-lg bg-[#0B0F19] border border-white/5">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={`text-xs ${notePriorityColors[note.priority] || notePriorityColors.normal}`}>{note.priority}</Badge>
                      <Badge className="text-xs bg-white/10 text-white/50">{noteTypeLabels[note.note_type] || note.note_type}</Badge>
                      <span className="text-xs text-white/30 ml-auto">{new Date(note.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-white/80">{note.text}</p>
                    <p className="text-xs text-white/30 mt-1">by {note.created_by_name} ({note.created_by_role})</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Counselor Overlay */}
          {viewerRole === 'counselor' && (
            <Card className="bg-[#131B2B] border-[#C89B7B]/10">
              <CardHeader className="border-b border-white/10 pb-3">
                <CardTitle className="text-sm font-medium text-[#C89B7B]">Counselor Tools</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between p-2 rounded bg-[#0B0F19]">
                  <span className="text-sm text-white/60">Unresolved support items</span>
                  <Badge className={support_summary?.open_tickets > 0 ? 'bg-amber-500/20 text-amber-400' : 'bg-white/10 text-white/40'}>
                    {support_summary?.open_tickets || 0}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Health Coach Overlay */}
          {viewerRole === 'health_coach' && (
            <Card className="bg-[#131B2B] border-[#4E7A6B]/10">
              <CardHeader className="border-b border-white/10 pb-3">
                <CardTitle className="text-sm font-medium text-[#4E7A6B]">Health Coach Tools</CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-center justify-between p-2 rounded bg-[#0B0F19]">
                  <span className="text-sm text-white/60">Protocol adherence</span>
                  <span className="text-sm text-white/80">{timeline_today?.completed_today || 0}/{timeline_today?.total_today || 0} today</span>
                </div>
                <div className="flex items-center justify-between p-2 rounded bg-[#0B0F19]">
                  <span className="text-sm text-white/60">Checked in today</span>
                  <span className={`text-sm ${timeline_today?.has_checked_in ? 'text-[#4E7A6B]' : 'text-amber-400'}`}>
                    {timeline_today?.has_checked_in ? 'Yes' : 'No'}
                  </span>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

// --- Sub-components ---

function AxisCard({ label, text, color }) {
  return (
    <div className={`p-3 rounded-lg bg-[#0B0F19] border border-${color}/20`}>
      <p className={`text-xs text-${color} uppercase tracking-wider mb-1 font-medium`}>{label}</p>
      <p className="text-sm text-white/70 line-clamp-3">{text || '-'}</p>
    </div>
  );
}

function EmptyState({ icon: Icon, text }) {
  return (
    <div className="text-center py-6">
      <Icon className="w-8 h-8 text-white/15 mx-auto mb-2" />
      <p className="text-sm text-white/40">{text}</p>
    </div>
  );
}

function QuickAction({ icon: Icon, label, to }) {
  return (
    <Link to={to}>
      <Button variant="outline" size="sm" className="border-white/10 text-white/70 hover:bg-white/5">
        <Icon className="w-4 h-4 mr-1" /> {label}
      </Button>
    </Link>
  );
}
